import { getAppMainScrollInnerElement } from './appMainScroll';
import { getDashboardHomeMobileScrollEl } from './dashboardHomeMobileScroll';

const DRAG_AUTO_SCROLL_EDGE = 80;
const DRAG_AUTO_SCROLL_MAX_SPEED = 18;

export const isVerticallyScrollable = (element) =>
	element instanceof Element && element.scrollHeight > element.clientHeight + 1;

const collectScrollContainers = (rootEl) => {
	const containers = [];
	let node = rootEl;
	while (node instanceof Element) {
		const { overflowY } = getComputedStyle(node);
		if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && isVerticallyScrollable(node)) {
			containers.push(node);
		}
		node = node.parentElement;
	}
	return containers;
};

export const resolveDragScrollTarget = (rootEl) => {
	if (!(rootEl instanceof Element)) return null;

	const mobileScroll = getDashboardHomeMobileScrollEl();
	if (mobileScroll?.contains(rootEl) && isVerticallyScrollable(mobileScroll)) {
		return mobileScroll;
	}

	let node = rootEl;
	while (node instanceof Element) {
		if (node.classList.contains('simplebar-content-wrapper')) {
			return node;
		}
		node = node.parentElement;
	}

	const mainScrollInner = getAppMainScrollInnerElement();
	if (mainScrollInner?.contains(rootEl)) {
		return mainScrollInner;
	}

	const containers = collectScrollContainers(rootEl);
	return containers[0] ?? null;
};

export const scrollForDragPointer = (clientY, scrollTarget) => {
	const viewportHeight = window.innerHeight;
	let delta = 0;

	if (clientY < DRAG_AUTO_SCROLL_EDGE) {
		const ratio = (DRAG_AUTO_SCROLL_EDGE - clientY) / DRAG_AUTO_SCROLL_EDGE;
		delta = -Math.max(4, Math.round(DRAG_AUTO_SCROLL_MAX_SPEED * ratio));
	} else if (clientY > viewportHeight - DRAG_AUTO_SCROLL_EDGE) {
		const ratio = (clientY - (viewportHeight - DRAG_AUTO_SCROLL_EDGE)) / DRAG_AUTO_SCROLL_EDGE;
		delta = Math.max(4, Math.round(DRAG_AUTO_SCROLL_MAX_SPEED * ratio));
	}

	if (delta === 0) return false;

	if (scrollTarget) {
		const maxScroll = scrollTarget.scrollHeight - scrollTarget.clientHeight;
		const nextTop = Math.min(maxScroll, Math.max(0, scrollTarget.scrollTop + delta));
		if (nextTop === scrollTarget.scrollTop) return false;
		scrollTarget.scrollTop = nextTop;
		return true;
	}

	window.scrollBy({ top: delta, behavior: 'auto' });
	return true;
};

/** 拖拽排序时靠近视口上下边缘自动滚动（首页 SimpleBar / 手机壳 / window 回退） */
export const attachDashboardDragAutoScroll = ({ getRootEl, isDraggingRef }) => {
	let lastClientY = 0;
	let autoScrollFrame = null;

	const runAutoScroll = () => {
		autoScrollFrame = null;
		if (!isDraggingRef.current) return;
		const scrollTarget = resolveDragScrollTarget(getRootEl());
		const didScroll = scrollForDragPointer(lastClientY, scrollTarget);
		if (didScroll && isDraggingRef.current) {
			autoScrollFrame = requestAnimationFrame(runAutoScroll);
		}
	};

	const scheduleAutoScroll = (event) => {
		if (typeof event.clientY === 'number') {
			lastClientY = event.clientY;
		}
		if (!autoScrollFrame) {
			autoScrollFrame = requestAnimationFrame(runAutoScroll);
		}
	};

	document.addEventListener('dragover', scheduleAutoScroll, { capture: true });
	document.addEventListener('drag', scheduleAutoScroll, { capture: true });

	return () => {
		document.removeEventListener('dragover', scheduleAutoScroll, { capture: true });
		document.removeEventListener('drag', scheduleAutoScroll, { capture: true });
		if (autoScrollFrame) {
			cancelAnimationFrame(autoScrollFrame);
		}
	};
};
